import { useEffect, useRef } from "react";

// MatrixPortrait — the source photo rendered as falling green "0"/"1"
// code, sampled from the photo's real brightness so the silhouette stays
// faithful (Matrix-movie digital-rain look, not a literal ASCII density
// ramp). Dark cells are skipped so the section's own background shows
// through, and a small percentage of characters flip each frame for the
// flicker/"alive" feel. Only animates while `active` is true.
const NOISE_INTERVAL_MS = 90;

export function MatrixPortrait({
  src,
  active = false,
  cols = 78,
  className = "",
  bg = "#050B06",
  fg = "57, 255, 90",
}) {
  const canvasRef = useRef(null);
  const lumRef = useRef(null);
  const charsRef = useRef(null);
  const rowsRef = useRef(0);
  const rafRef = useRef(0);
  const lastRef = useRef(0);

  // Sample the source image once — brightness per cell, drives both
  // whether a cell renders at all and how bright its green glow is.
  useEffect(() => {
    const img = new Image();
    img.crossOrigin = "anonymous";
    img.src = src;
    img.onload = () => {
      const aspect = img.naturalHeight / img.naturalWidth;
      const rows = Math.round(cols * aspect * 0.5);
      rowsRef.current = rows;

      const off = document.createElement("canvas");
      off.width = cols;
      off.height = rows;
      const octx = off.getContext("2d");
      octx.drawImage(img, 0, 0, cols, rows);
      const data = octx.getImageData(0, 0, cols, rows).data;

      const lum = new Float32Array(cols * rows);
      const chars = new Uint8Array(cols * rows);
      for (let i = 0, p = 0; i < lum.length; i++, p += 4) {
        lum[i] = (0.299 * data[p] + 0.587 * data[p + 1] + 0.114 * data[p + 2]) / 255;
        chars[i] = Math.random() < 0.5 ? 0 : 1;
      }
      lumRef.current = lum;
      charsRef.current = chars;
    };
  }, [src, cols]);

  useEffect(() => {
    if (!active) {
      cancelAnimationFrame(rafRef.current);
      return;
    }

    const canvas = canvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext("2d");

    const setupAndDraw = () => {
      const lum = lumRef.current;
      const chars = charsRef.current;
      const rows = rowsRef.current;
      if (!lum || !rows) return;

      const dpr = window.devicePixelRatio || 1;
      const w = canvas.clientWidth;
      const h = canvas.clientHeight;
      canvas.width = Math.floor(w * dpr);
      canvas.height = Math.floor(h * dpr);
      ctx.setTransform(dpr, 0, 0, dpr, 0, 0);

      const charW = w / cols;
      const charH = h / rows;
      const fontSize = Math.max(6, Math.floor(charH * 1.05));
      ctx.font = `${fontSize}px "JetBrains Mono", ui-monospace, monospace`;
      ctx.textBaseline = "top";

      const paintFrame = () => {
        ctx.fillStyle = bg;
        ctx.fillRect(0, 0, w, h);
        for (let y = 0; y < rows; y++) {
          for (let x = 0; x < cols; x++) {
            const i = y * cols + x;
            const l = lum[i];
            if (l < 0.1) continue; // dark region — let bg show, keeps silhouette
            if (Math.random() < 0.06) chars[i] = chars[i] ? 0 : 1;
            const alpha = Math.min(1, 0.28 + l * 0.85);
            ctx.fillStyle = `rgba(${fg}, ${alpha.toFixed(2)})`;
            ctx.fillText(chars[i] ? "1" : "0", x * charW, y * charH);
          }
        }
      };

      const loop = (t) => {
        if (t - lastRef.current >= NOISE_INTERVAL_MS) {
          lastRef.current = t;
          paintFrame();
        }
        rafRef.current = requestAnimationFrame(loop);
      };
      paintFrame();
      rafRef.current = requestAnimationFrame(loop);
    };

    if (lumRef.current) {
      setupAndDraw();
    } else {
      const id = setInterval(() => {
        if (lumRef.current) {
          clearInterval(id);
          setupAndDraw();
        }
      }, 50);
      return () => clearInterval(id);
    }

    return () => cancelAnimationFrame(rafRef.current);
  }, [active, cols, bg, fg]);

  return (
    <canvas
      ref={canvasRef}
      className={className}
      aria-hidden="true"
      data-testid="matrix-portrait"
    />
  );
}
